/*
 * Progressive enhancement for the featured-category booster pack.
 * The plain link grid is the default. Motion is enabled only when requested
 * by the section and welcome on the device, and listeners exist only while
 * the section is near the viewport.
 */
if ('customElements' in window && !customElements.get('pack-scene')) {
  customElements.define(
    'pack-scene',
    class PackScene extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;
        this.root = this.querySelector('[data-pack-scene]');
        this.track = this.querySelector('[data-track]');
        this.stage = this.querySelector('[data-stage]');
        this.pack = this.querySelector('[data-pack]');
        this.flap = this.querySelector('[data-flap]');
        this.hint = this.querySelector('[data-hint]');
        this.dim = this.querySelector('[data-dim]');
        this.heading = this.querySelector('[data-title]');
        this.skip = this.querySelector('[data-skip-scene]');
        this.grid = this.querySelector('.tiles__grid');
        this.items = [...this.querySelectorAll('[data-item]')];
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.schedule = this.schedule.bind(this);
        this.skipAnimation = this.skipAnimation.bind(this);
        this.motionChanged = this.motionChanged.bind(this);

        const cannotAnimate =
          this.dataset.enabled !== 'true' ||
          this.motion.matches ||
          navigator.connection?.saveData ||
          !('IntersectionObserver' in window) ||
          !this.root ||
          !this.track ||
          !this.stage ||
          !this.pack ||
          !this.flap ||
          !this.hint ||
          !this.dim ||
          !this.heading ||
          !this.skip ||
          !this.grid ||
          !this.items.length;

        if (cannotAnimate) {
          this.root?.classList.remove('is-scene-pending');
          this.dataset.enhanced = '';
          return;
        }

        this.motion.addEventListener?.('change', this.motionChanged);
        this.setInteractive(this.grid, false);
        this.items.forEach((item) => this.setInteractive(item, false));
        this.root.classList.remove('is-scene-pending');
        this.root.classList.add('is-scene');
        this.dataset.enhanced = '';
        this.skip.addEventListener('click', this.skipAnimation);

        this.observer = new IntersectionObserver(
          ([entry]) => (entry.isIntersecting ? this.activate() : this.deactivate()),
          { rootMargin: '100% 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.destroy();
        this.initialised = false;
      }

      static segment(value, from, to) {
        if (to === from) return 0;
        return Math.min(1, Math.max(0, (value - from) / (to - from)));
      }

      setInteractive(item, interactive) {
        item.toggleAttribute('inert', !interactive);
        item.setAttribute('aria-hidden', interactive ? 'false' : 'true');
      }

      activate() {
        if (this.active) return;
        this.active = true;
        this.root.classList.add('is-active');
        window.addEventListener('scroll', this.schedule, { passive: true });
        window.addEventListener('resize', this.schedule, { passive: true });
        this.draw();
      }

      deactivate() {
        if (!this.active) return;
        this.active = false;
        this.root?.classList.remove('is-active');
        window.removeEventListener('scroll', this.schedule);
        window.removeEventListener('resize', this.schedule);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      schedule() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.draw();
        });
      }

      get progress() {
        const rect = this.track.getBoundingClientRect();
        const travel = this.track.offsetHeight - this.stage.offsetHeight;
        return travel <= 0 ? 0 : PackScene.segment(-rect.top, 0, travel);
      }

      draw() {
        const progress = this.progress;
        const segment = PackScene.segment;
        const wobble = segment(progress, 0, 0.18);
        const tilt = Math.sin(wobble * Math.PI * 2) * 2.5;
        const packFade = 1 - segment(progress, 0.55, 0.78) * 0.45;

        this.pack.style.transform = `rotate(${tilt.toFixed(2)}deg)`;
        this.pack.style.opacity = packFade.toFixed(3);

        const tear = segment(progress, 0.18, 0.42);
        this.flap.style.transform =
          `translate(${(-180 * tear).toFixed(1)}px, ${(-420 * tear).toFixed(1)}px) rotate(${(-38 * tear).toFixed(1)}deg)`;
        this.flap.style.opacity = (1 - segment(progress, 0.46, 0.56)).toFixed(3);
        this.hint.style.opacity = (1 - segment(progress, 0, 0.12)).toFixed(3);
        this.dim.style.opacity = (segment(progress, 0.55, 0.78) * 0.5).toFixed(3);
        // The category label stays readable after the cards land. Fading the
        // pack is enough depth change; fading the heading made the final state
        // look disabled and weakened its contrast.
        this.heading.style.opacity = '1';

        const compact = window.innerWidth < 640;
        const pair = this.items.length === 2;
        const spread = pair
          ? Math.min(180, window.innerWidth * 0.24)
          : Math.min(300, window.innerWidth * (compact ? 0.325 : 0.3));
        const mouth = -this.pack.getBoundingClientRect().height / 2;
        const starts = [0.3, 0.39, 0.48];

        let hasInteractiveItem = false;
        this.items.forEach((item, index) => {
          // A pair leaves the pack together, mirrored throughout the reveal.
          const start = pair ? 0.34 : starts[index] ?? 0.3 + index * 0.09;
          const reveal = segment(progress, start, start + 0.32);
          const lane = (index - (this.items.length - 1) / 2) * (pair ? 2 : 1);
          const x = lane * spread * reveal;
          const y = mouth * (1 - reveal);
          const scale = 0.4 + reveal * 0.6;
          const rotate = lane * (compact ? 2.5 : 7) * reveal;

          item.style.transform =
            `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${scale.toFixed(3)}) rotate(${rotate.toFixed(1)}deg)`;
          item.style.opacity = reveal.toFixed(3);
          const interactive = reveal >= 0.9;
          this.setInteractive(item, interactive);
          if (interactive) hasInteractiveItem = true;
        });
        this.setInteractive(this.grid, hasInteractiveItem);
      }

      motionChanged(event) {
        if (event.matches) this.disable();
      }

      skipAnimation() {
        const firstLink = this.querySelector('.tiles__link[href]');
        this.disable();
        firstLink?.focus();
      }

      disable() {
        this.destroy();
        this.root?.classList.remove('is-scene', 'is-scene-pending', 'is-active');
        [this.pack, this.flap, this.hint, this.dim, this.heading, ...this.items].forEach((element) =>
          element?.removeAttribute('style')
        );
        this.grid?.removeAttribute('inert');
        this.grid?.removeAttribute('aria-hidden');
        this.items.forEach((item) => {
          item.removeAttribute('inert');
          item.removeAttribute('aria-hidden');
        });
      }

      destroy() {
        this.deactivate();
        this.observer?.disconnect();
        this.observer = null;
        this.skip?.removeEventListener('click', this.skipAnimation);
        this.motion?.removeEventListener?.('change', this.motionChanged);
      }
    }
  );
}
